import { Navigate } from 'react-router-dom';
import { ReactNode, Suspense } from 'react';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { BeneficiariesContent } from 'pages/BeneficiariesContent';
import { RequireAuth } from 'features/RequireAuth/RequireAuth';
import { BeneficiariesDetails } from 'pages/BeneficiariesDetails';
import { EmployedsContent } from 'pages/EmployedsContent';
import { EmployedsDetails } from 'pages/EmployedsDetails';
import { OperationsContent } from 'pages/OperationsContent';
import { OperationsDealDetails } from 'pages/OperationsDealDetails';
import { OperationsPaymentDetails } from 'pages/OperationsPaymentDetails';
import { TaxeDetails } from 'pages/TaxeDetails';
import { OrdersContent } from 'pages/OrdersContent';
import { OrdersDetails } from 'pages/OrdersDetails';
import { CreateOrder } from 'pages/CreateOrder';
import { Profile } from 'pages/Profile';
import { Layout } from 'features/Layouts/Layouts';
import { Taxes } from 'pages/Taxes';
import { BeneficiarCreate } from 'pages/BeneficiarCreate';
import { Notifications } from 'pages/Notifications';
import { Login } from 'pages/Login/Login';
import { RegistrationStep1 } from 'pages/Registration/RegistrationStep1';
import { RegistrationStep2 } from 'pages/Registration/RegistrationStep2';
import { RegistrationStep3 } from 'pages/Registration/RegistrationStep3';
import { RegistrationStep4 } from 'pages/Registration/RegistrationStep4';
import { RegistrationStep5 } from 'pages/Registration/RegistrationStep5';
import { ForgotPassword } from 'pages/ForgotPassword/ForgotPassword';
import { EmployedsWorkSupportContent } from 'pages/EmployedsWorkSupportContent';
import { PerformersContent } from 'pages/PerformersContent';
import { ChecksContent } from 'pages/ChecksContent';
import { UncreditedPaymentsContent } from 'pages/UncreditedPaymentsContent';
import { Chat } from 'pages/Chat';
import { ContractsContent } from 'pages/ContractsContent';
import { EmployedsProfileContent } from 'pages/EmployedsProfileContent';
import { ContractsCreate } from 'pages/ContractsCreate';
import { Knowledge } from 'pages/Knowledge';
import { Users } from 'pages/Users';
import { AlertsWorkSupport } from 'pages/AlertsWorkSupport';

// eslint-disable-next-line react/no-children-prop
const withAuth = (jsx: ReactNode, roles?: string[]) => (
  <RequireAuth roles={roles}>{jsx}</RequireAuth>
);

export const routes = [
  { path: '/', element: <Navigate to='login' replace /> },
  {
    path: '/login',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <Login />
      </Suspense>
    ),
  },
  {
    path: '/registration/step1',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <RegistrationStep1 />
      </Suspense>
    ),
  },
  {
    path: '/registration/step2',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <RegistrationStep2 />
      </Suspense>
    ),
  },
  {
    path: '/registration/step3',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <RegistrationStep3 />
      </Suspense>
    ),
  },
  {
    path: '/registration/step4',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <RegistrationStep4 />
      </Suspense>
    ),
  },
  {
    path: '/registration/step5',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <RegistrationStep5 />
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <ForgotPassword />
      </Suspense>
    ),
  },
  {
    path: '/orders',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <OrdersContent />
      </Suspense>,
    ),
  },
  {
    path: '/orders/create',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <CreateOrder />
      </Suspense>,
    ),
  },
  {
    path: '/orders/:id',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <OrdersDetails />
      </Suspense>,
    ),
  },
  {
    path: '/beneficiaries',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <BeneficiariesContent />
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/beneficiaries/:id',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <BeneficiariesDetails />
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/employees',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <EmployedsContent />
      </Suspense>,
    ),
  },
  {
    path: '/employees/:id',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <EmployedsProfileContent />
      </Suspense>,
    ),
  },
  {
    path: '/contracts',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <ContractsContent />
      </Suspense>,
    ),
  },
  {
    path: '/contracts/create',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <ContractsCreate />
      </Suspense>,
    ),
  },
  {
    path: '/employedsWorkSupport',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <EmployedsWorkSupportContent />
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/performersWorkSupport',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <PerformersContent />
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/employedsWorkSupport/:id',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <EmployedsDetails />
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/operations',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <OperationsContent />
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/operations/deals/:id',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <OperationsDealDetails />
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/operations/payments/:id',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <OperationsPaymentDetails />
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/operations/taxes/:id',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Layout>
          <TaxeDetails />
        </Layout>
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/taxes',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Layout>
          <Taxes />
        </Layout>
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/alerts',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Layout>
          <AlertsWorkSupport />
        </Layout>
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/taxes/:id',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Layout>
          <TaxeDetails />
        </Layout>
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/users',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Layout>
          <Users />
        </Layout>
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/profile',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Profile />
      </Suspense>,
    ),
  },
  {
    path: '/profile/:key',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Profile />
      </Suspense>,
    ),
  },
  {
    path: '/profile/security',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Profile />
      </Suspense>,
    ),
  },
  {
    path: '/beneficiar/create',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Layout>
          <BeneficiarCreate />
        </Layout>
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/notifications',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Layout>
          <Notifications />
        </Layout>
      </Suspense>,
    ),
  },
  {
    path: '/checks',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <ChecksContent />
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/uncredited-payments',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <UncreditedPaymentsContent />
      </Suspense>,
      ['worksupport'],
    ),
  },
  {
    path: '/chat',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Chat key='customerCabinet' version='customerCabinet' title='Поддержка' />
      </Suspense>,
    ),
  },
  {
    path: '/chat/*',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Chat key='customerCabinet' version='customerCabinet' title='Поддержка' />
      </Suspense>,
    ),
  },
  {
    path: '/orderchat',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Chat key='orderChat' version='orderChat' title='Чаты по заказам' />
      </Suspense>,
    ),
  },
  {
    path: '/orderchat/*',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Chat key='orderChat' version='orderChat' title='Чаты по заказам' />
      </Suspense>,
    ),
  },
  {
    path: '/knowledge',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Knowledge />
      </Suspense>,
    ),
  },
  {
    path: '/knowledge/*',
    element: withAuth(
      <Suspense fallback={<LoadingSkeleton />}>
        <Knowledge />
      </Suspense>,
    ),
  },
  { path: '*', element: <Navigate to='/' replace /> },
];
